import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  MenuItem,
  InputAdornment
} from '@mui/material';
import {
  RefreshOutlined,
  AddOutlined,
  SearchOutlined,
  VisibilityOutlined,
  AccountBalanceOutlined,
  CheckCircleOutlined,
  HourglassEmptyOutlined,
  ErrorOutlined,
  PauseCircleOutlined,
  ClearOutlined,
  CalendarTodayOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updateScreenTitle } from '../../../LayoutContainers/AppLayout/redux/AppSlice';

// Mock data
import {
  getTaxServiceRequests
} from '../../../mockData/orgDocIndex';

// Status configuration
const TaxRequestStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
};

const TaxRequestStatusLabels = {
  [TaxRequestStatus.PENDING]: 'Pending',
  [TaxRequestStatus.IN_PROGRESS]: 'In Progress',
  [TaxRequestStatus.COMPLETED]: 'Completed',
  [TaxRequestStatus.CANCELLED]: 'Cancelled'
};

const TaxRequestStatusColors = {
  [TaxRequestStatus.PENDING]: '#FF9800',
  [TaxRequestStatus.IN_PROGRESS]: '#2196F3',
  [TaxRequestStatus.COMPLETED]: '#4CAF50',
  [TaxRequestStatus.CANCELLED]: '#f44336'
};

// Mock Badge
const MockBadge = () => (
  <Chip
    label="MOCK"
    size="small"
    color="warning"
    sx={{ ml: 1, fontSize: '10px', height: '20px' }}
  />
);

/**
 * Status Chip Component
 */
const StatusChip = ({ status }) => {
  const getStatusIcon = () => {
    switch (status) {
      case TaxRequestStatus.COMPLETED:
        return <CheckCircleOutlined fontSize="small" />;
      case TaxRequestStatus.IN_PROGRESS:
        return <HourglassEmptyOutlined fontSize="small" />;
      case TaxRequestStatus.CANCELLED:
        return <ErrorOutlined fontSize="small" />;
      case TaxRequestStatus.PENDING:
        return <HourglassEmptyOutlined fontSize="small" />;
      default:
        return null;
    }
  };

  return (
    <Chip
      icon={getStatusIcon()}
      label={TaxRequestStatusLabels[status] || status}
      size="small"
      sx={{
        bgcolor: `${TaxRequestStatusColors[status]}20`,
        color: TaxRequestStatusColors[status],
        fontWeight: 'medium',
        '& .MuiChip-icon': {
          color: 'inherit'
        }
      }}
    />
  );
};

/**
 * Summary Tile Component
 */
const SummaryTile = ({ title, count, icon, color, isActive, onClick }) => (
  <Card
    sx={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s',
      border: isActive ? `2px solid ${color}` : '2px solid transparent',
      '&:hover': onClick ? {
        boxShadow: 3,
        transform: 'translateY(-2px)'
      } : {}
    }}
    onClick={onClick}
  >
    <CardContent sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={color}>
            {count}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 1,
            borderRadius: 1,
            bgcolor: `${color}15`,
            color: color
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

/**
 * Quick Filter Chips
 */
const QuickFilterChips = ({ filters, selectedFilter, onFilterChange }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
    {filters.map((filter) => (
      <Chip
        key={filter.key}
        label={`${filter.label} (${filter.count})`}
        onClick={() => onFilterChange(filter.key)}
        variant={selectedFilter === filter.key ? 'filled' : 'outlined'}
        color={selectedFilter === filter.key ? filter.color : 'default'}
        size="small"
      />
    ))}
  </Box>
);

/**
 * Date Range Presets
 */
const DATE_PRESETS = [
  { key: 'all', label: 'All Time', days: null },
  { key: '7d', label: 'Last 7 Days', days: 7 },
  { key: '30d', label: 'Last 30 Days', days: 30 },
  { key: '90d', label: 'Last 90 Days', days: 90 }
];

/**
 * Empty State Component
 */
const EmptyState = ({ type, title, description, primaryActionLabel, onPrimaryAction }) => (
  <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
    <AccountBalanceOutlined sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      {description}
    </Typography>
    {primaryActionLabel && (
      <Button variant="contained" onClick={onPrimaryAction}>
        {primaryActionLabel}
      </Button>
    )}
  </Box>
);

/**
 * My Tax Requests Page
 *
 * Shows tax service requests history.
 *
 * Features:
 * - Summary tiles with click-to-filter
 * - Quick filter chips
 * - Searchable/filterable request table
 * - Date range filter
 * - Pagination
 */
const MyTaxRequestsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [datePreset, setDatePreset] = useState('all');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // TODO: Get actual organization ID from context/auth
  const organizationId = 'org-001';

  // Set screen title
  useEffect(() => {
    dispatch(updateScreenTitle('My Tax Requests'));
    return () => dispatch(updateScreenTitle(''));
  }, [dispatch]);

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      const taxRequests = getTaxServiceRequests(organizationId);
      setRequests(taxRequests);
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter requests
  const filteredRequests = useMemo(() => {
    return requests.filter(request => {
      // Search filter
      const matchesSearch = !searchTerm ||
        request.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.serviceName.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus = statusFilter === 'ALL' || request.status === statusFilter;

      // Date filter
      let matchesDate = true;
      if (datePreset !== 'all') {
        const preset = DATE_PRESETS.find(p => p.key === datePreset);
        if (preset && preset.days) {
          const cutoffDate = new Date();
          cutoffDate.setDate(cutoffDate.getDate() - preset.days);
          const requestDate = new Date(request.requestedAt);
          matchesDate = requestDate >= cutoffDate;
        }
      }

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [requests, searchTerm, statusFilter, datePreset]);

  // Calculate status counts for quick filters
  const statusCounts = useMemo(() => ({
    ALL: requests.length,
    [TaxRequestStatus.PENDING]: requests.filter(r => r.status === TaxRequestStatus.PENDING).length,
    [TaxRequestStatus.IN_PROGRESS]: requests.filter(r => r.status === TaxRequestStatus.IN_PROGRESS).length,
    [TaxRequestStatus.COMPLETED]: requests.filter(r => r.status === TaxRequestStatus.COMPLETED).length,
    [TaxRequestStatus.CANCELLED]: requests.filter(r => r.status === TaxRequestStatus.CANCELLED).length
  }), [requests]);

  // Quick filter chips configuration
  const quickFilters = [
    { key: 'ALL', label: 'All', count: statusCounts.ALL, color: 'primary' },
    { key: TaxRequestStatus.PENDING, label: 'Pending', count: statusCounts[TaxRequestStatus.PENDING], color: 'warning' },
    { key: TaxRequestStatus.IN_PROGRESS, label: 'In Progress', count: statusCounts[TaxRequestStatus.IN_PROGRESS], color: 'info' },
    { key: TaxRequestStatus.COMPLETED, label: 'Completed', count: statusCounts[TaxRequestStatus.COMPLETED], color: 'success' },
    { key: TaxRequestStatus.CANCELLED, label: 'Cancelled', count: statusCounts[TaxRequestStatus.CANCELLED], color: 'error' }
  ];

  // Paginated requests
  const paginatedRequests = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRequests.slice(start, start + rowsPerPage);
  }, [filteredRequests, page, rowsPerPage]);

  // Handlers
  const handleViewRequest = (request) => {
    // Navigate to ticket detail view if available
    navigate(`/ADM-Ticket/ticket-detail-view/${request.id}`);
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    setDatePreset('all');
    setPage(0);
  };

  const handleQuickFilter = (filterKey) => {
    setStatusFilter(filterKey);
    setPage(0);
  };

  const handleSummaryTileClick = (status) => {
    setStatusFilter(status);
    setPage(0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm || statusFilter !== 'ALL' || datePreset !== 'all';

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccountBalanceOutlined fontSize="large" color="primary" />
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
              My Tax Requests
              <MockBadge />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and track your tax service requests
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh}>
              <RefreshOutlined />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={() => navigate('/CST-TaxServices/list')}
          >
            Request Service
          </Button>
        </Box>
      </Box>

      {/* Summary Tiles - Clickable */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <SummaryTile
            title="Total Requests"
            count={requests.length}
            icon={<AccountBalanceOutlined />}
            color="#2196F3"
            isActive={statusFilter === 'ALL'}
            onClick={() => handleSummaryTileClick('ALL')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryTile
            title="Pending"
            count={statusCounts[TaxRequestStatus.PENDING]}
            icon={<HourglassEmptyOutlined />}
            color="#FF9800"
            isActive={statusFilter === TaxRequestStatus.PENDING}
            onClick={() => handleSummaryTileClick(TaxRequestStatus.PENDING)}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryTile
            title="In Progress"
            count={statusCounts[TaxRequestStatus.IN_PROGRESS]}
            icon={<HourglassEmptyOutlined />}
            color="#2196F3"
            isActive={statusFilter === TaxRequestStatus.IN_PROGRESS}
            onClick={() => handleSummaryTileClick(TaxRequestStatus.IN_PROGRESS)}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryTile
            title="Completed"
            count={statusCounts[TaxRequestStatus.COMPLETED]}
            icon={<CheckCircleOutlined />}
            color="#4CAF50"
            isActive={statusFilter === TaxRequestStatus.COMPLETED}
            onClick={() => handleSummaryTileClick(TaxRequestStatus.COMPLETED)}
          />
        </Grid>
      </Grid>

      {/* Quick Filter Chips */}
      <QuickFilterChips
        filters={quickFilters}
        selectedFilter={statusFilter}
        onFilterChange={handleQuickFilter}
      />

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by request ID or service..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearOutlined fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Date Preset */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Date Range"
                value={datePreset}
                onChange={(e) => {
                  setDatePreset(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayOutlined fontSize="small" />
                    </InputAdornment>
                  )
                }}
              >
                {DATE_PRESETS.map(preset => (
                  <MenuItem key={preset.key} value={preset.key}>
                    {preset.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Actions */}
            <Grid item xs={12} sm={12} md={5}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                {hasActiveFilters && (
                  <Button
                    size="small"
                    startIcon={<ClearOutlined />}
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>
                )}
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredRequests.length} of {requests.length} requests
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        {filteredRequests.length === 0 ? (
          <EmptyState
            type={hasActiveFilters ? 'no-results' : 'no-requests'}
            title={hasActiveFilters ? 'No matching requests' : 'No requests yet'}
            description={
              hasActiveFilters
                ? 'Try adjusting your search or filter criteria to find what you\'re looking for.'
                : 'Get started by requesting a tax service for your organization.'
            }
            primaryActionLabel={hasActiveFilters ? 'Clear Filters' : 'Browse Services'}
            onPrimaryAction={hasActiveFilters ? handleClearFilters : () => navigate('/CST-TaxServices/list')}
          />
        ) : (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Request ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tax Period</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Payment</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Requested</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRequests.map((request) => (
                    <TableRow
                      key={request.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td': { borderBottom: 0 }
                      }}
                      onClick={() => handleViewRequest(request)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {request.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{request.serviceName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.serviceCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {request.taxPeriod || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={request.status} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          AED {request.totalCharge?.toLocaleString() || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.paymentStatus}
                          size="small"
                          color={request.paymentStatus === 'PAID' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(request.requestedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRequest(request);
                            }}
                          >
                            <VisibilityOutlined />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredRequests.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          </>
        )}
      </Card>

      {/* Info Note */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          This page shows tax service requests (Corporate Tax, VAT) for your organization.
        </Typography>
      </Box>
    </Box>
  );
};

export default MyTaxRequestsPage;
